import { useState } from 'react';
import { Form } from 'react-bootstrap';
import PhoneInput, { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import { useOrganizationProfile } from '../hooks/useOrganizationProfile';
import { resolveCountryCode } from '../utils/countriesStates';

export { isValidPhoneNumber };

/** A number as plain E.164 ("+919876543210"), reading digits with no country code in `country`; the text itself when it can't be read. */
export function toE164(value, country = 'IN') {
  if (!value) return '';
  if (value.startsWith('+')) return value;
  try {
    return parsePhoneNumber(value, country)?.number || value;
  } catch (e) {
    return value;
  }
}

/**
 * Reusable phone number input with a country-code dropdown, live formatting, and validation
 * (wraps react-phone-number-input / libphonenumber-js - phone formats vary too much by country
 * to hand-roll). Stores/emits plain E.164 strings (e.g. "+919876543210"), so no schema change
 * and no separate "country code" field is needed anywhere this is used.
 *
 * Synthesizes a DOM-event-shaped onChange ({ target: { name, value } }) instead of the library's
 * native (value) => void signature, so every existing handler in this codebase (a generic
 * handleXInputChange(e) or a one-off (e) => setXFormData({...xFormData, field: e.target.value}))
 * keeps working unmodified - only the input element itself needs to change at each call site.
 *
 * When `defaultCountry` isn't passed, defaults to the logged-in user's own organization's
 * country (via the existing useOrganizationProfile/resolveCountryCode helpers) rather than
 * requiring every form to plumb that through itself.
 *
 * The country the user picks is remembered and becomes the default: the library resets to the
 * default country whenever the number is cleared, which would otherwise undo their choice.
 */
function PhoneNumberInput({ name, value, onChange, defaultCountry, disabled, required, id, invalid, autoComplete = 'tel', placeholder }) {
  const [touched, setTouched] = useState(false);
  const [chosenCountry, setChosenCountry] = useState(null);
  const organization = useOrganizationProfile();

  // Signed-out pages (the online store's checkout) have no organization: iBigDo's market is India
  const resolvedDefaultCountry = defaultCountry || resolveCountryCode(organization?.country) || 'IN';
  const country = chosenCountry || (resolvedDefaultCountry === 'OTHER' ? undefined : resolvedDefaultCountry);

  // A number saved before this input existed is plain digits ("98765 43210"): read it in the default country
  // so it shows in the box, instead of the box looking empty
  let shown = value || undefined;
  if (shown && !shown.startsWith('+')) {
    try {
      shown = parsePhoneNumber(shown, country)?.number || undefined;
    } catch (e) {
      shown = undefined;
    }
  }

  const isInvalid = invalid || (touched && !!value && !isValidPhoneNumber(shown || ''));

  return (
    <>
      <PhoneInput
        name={name}
        value={shown}
        onChange={(newValue) => onChange({ target: { name, value: newValue || '' } })}
        onCountryChange={(picked) => { if (picked) setChosenCountry(picked); }}
        onBlur={() => setTouched(true)}
        defaultCountry={country}
        disabled={disabled}
        required={required}
        id={id}
        autoComplete={autoComplete}
        placeholder={placeholder}
        className={isInvalid ? 'is-invalid' : ''}
      />
      {isInvalid && (
        <Form.Text className="text-danger">Doesn't look like a valid phone number</Form.Text>
      )}
    </>
  );
}

export default PhoneNumberInput;
